import * as React from 'react'
import { CalendarIcon } from 'lucide-react'
import { usePage } from '@inertiajs/react'

import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { CalendarGrid, MONTHS_FULL, isSameDay } from '@/shared/ui/calendar-grid'
import { nowInTimeZone } from '@/shared/lib/tenantDateTime'
import {
  formatHourMinute,
  fromTwelveHourClockParts,
  parseWallClockParts,
  toCanonicalWallClock,
  usesTwelveHourClock,
  type TenantTimeFormatInput,
} from '@/shared/ui/wall-clock-input-utils'
import type { SharedProps } from '@/shared/types/SharedProps'
import TenantTimeInput from '@/shared/ui/tenant-time-input'
import { t } from '@/shared/lib/i18n'

interface DateTimePickerInputProps {
  id?: string
  value: string // "YYYY-MM-DDTHH:MM" or ""
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  inputClassName?: string
  required?: boolean
  'aria-invalid'?: boolean
  maxDate?: Date
  /** IANA timezone (e.g. tenant's configured timezone) used to resolve "today"/"now". Defaults to the browser's local timezone. */
  timeZone?: string
  /** Override shared Inertia prop (tests / isolated previews). */
  tenantTimeFormat?: TenantTimeFormatInput
  /**
   * When false, the "Now" quick-action button is hidden inside the calendar popover.
   * Defaults to true. Set false on the NC Resolve form (REBUILD-373).
   */
  showNowButton?: boolean
}

function parseDateTime(value: string): { date: Date | undefined; hours: string; minutes: string } {
  if (!value) return { date: undefined, hours: '', minutes: '' }
  const [datePart, timePart = ''] = value.split('T')
  const [year, month, day] = datePart.split('-').map(Number)
  const [h = '', m = ''] = timePart.split(':')
  return {
    date: new Date(year, month - 1, day),
    hours: h,
    minutes: m,
  }
}

function toDatetimeLocal(date: Date, hours: string, minutes: string): string {
  const y = date.getFullYear()
  const mo = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const h = (hours || '00').padStart(2, '0')
  const mi = (minutes || '00').padStart(2, '0')
  return `${y}-${mo}-${d}T${h}:${mi}`
}

const MASK_SEPARATORS_24: Record<number, string> = { 2: '/', 5: '/', 10: ' ', 13: ':' }

/** Index of the first digit at or after `from` in `str`, or `str.length` if none exists. */
function nextDigitSlot(str: string, from: number): number {
  for (let i = from; i < str.length; i++) {
    if (/\d/.test(str[i])) return i
  }
  return str.length
}

/** Index of the last digit strictly before `before` in `str`, scanning right-to-left; returns 0 if none. */
function prevDigitSlot(str: string, before: number): number {
  for (let i = before - 1; i >= 0; i--) {
    if (/\d/.test(str[i])) return i
  }
  return 0
}

/**
 * Maps a digit-count caret from a raw (unmasked) input string into the
 * corresponding position in the masked 24h string.
 */
function caretPositionInMasked24(masked: string, digitCount: number): number {
  if (digitCount === 0) return 0
  let count = 0
  for (let i = 0; i < masked.length; i++) {
    if (/\d/.test(masked[i])) {
      count++
      if (count === digitCount) return i + 1
    }
  }
  return masked.length
}

function applyMask24(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 12)
  let result = ''
  let digitIndex = 0
  for (let pos = 0; pos < 16 && digitIndex < digits.length; pos++) {
    if (MASK_SEPARATORS_24[pos]) {
      result += MASK_SEPARATORS_24[pos]
    } else {
      result += digits[digitIndex]
      digitIndex++
    }
  }
  return result
}

function parseMaskedInput24(masked: string): { date: Date; hours: string; minutes: string } | null {
  const digits = masked.replace(/\D/g, '')
  if (digits.length < 12) return null
  const month = parseInt(digits.slice(0, 2), 10)
  const day = parseInt(digits.slice(2, 4), 10)
  const year = parseInt(digits.slice(4, 8), 10)
  const hoursNum = parseInt(digits.slice(8, 10), 10)
  const minutesNum = parseInt(digits.slice(10, 12), 10)
  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  if (hoursNum > 23 || minutesNum > 59) return null
  const date = new Date(year, month - 1, day)
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return null
  return { date, hours: String(hoursNum), minutes: String(minutesNum) }
}

function parseMaskedInput12(masked: string): { date: Date; hours: string; minutes: string } | null {
  const match = masked
    .trim()
    .match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!match) return null

  const month = Number(match[1])
  const day = Number(match[2])
  const year = Number(match[3])
  const hourTwelve = Number(match[4])
  const minutesNum = Number(match[5])
  const period = match[6].toUpperCase() === 'PM' ? 'PM' : 'AM'

  if (month < 1 || month > 12 || day < 1 || day > 31) return null
  if (hourTwelve < 1 || hourTwelve > 12 || minutesNum > 59) return null

  const { hour, minute } = fromTwelveHourClockParts({
    hour: hourTwelve,
    minute: minutesNum,
    period,
  })
  const date = new Date(year, month - 1, day)
  if (date.getMonth() !== month - 1 || date.getDate() !== day) return null
  return { date, hours: String(hour), minutes: String(minute) }
}

function formatForDisplay(
  date: Date | undefined,
  hours: string,
  minutes: string,
  tenantTimeFormat: TenantTimeFormatInput,
): string {
  if (!date) return ''
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const year = date.getFullYear()
  const hourNum = hours === '' ? 0 : parseInt(hours, 10) || 0
  const minuteNum = minutes === '' ? 0 : parseInt(minutes, 10) || 0
  return `${month}/${day}/${year} ${formatHourMinute(hourNum, minuteNum, tenantTimeFormat)}`
}

function defaultPlaceholder(tenantTimeFormat: TenantTimeFormatInput): string {
  return usesTwelveHourClock(tenantTimeFormat) ? 'MM/DD/YYYY h:MM AM' : 'MM/DD/YYYY HH:MM'
}

export function DateTimePickerInput({
  id,
  value,
  onChange,
  placeholder,
  disabled,
  className,
  inputClassName,
  required,
  'aria-invalid': ariaInvalid,
  maxDate,
  timeZone,
  tenantTimeFormat: tenantTimeFormatProp,
  showNowButton = true,
}: DateTimePickerInputProps) {
  const { tenantTimeFormat: sharedTenantTimeFormat } = usePage<SharedProps>().props
  const tenantTimeFormat = tenantTimeFormatProp ?? sharedTenantTimeFormat
  const isTwelveHour = usesTwelveHourClock(tenantTimeFormat)
  const resolvedPlaceholder = placeholder ?? defaultPlaceholder(tenantTimeFormat)

  const [open, setOpen] = React.useState(false)
  const today = React.useMemo(() => (timeZone ? nowInTimeZone(timeZone) : new Date()), [timeZone])

  const { date: selectedDate, hours: initHours, minutes: initMinutes } = parseDateTime(value)

  const [viewYear, setViewYear] = React.useState(
    () => selectedDate?.getFullYear() ?? today.getFullYear(),
  )
  const [viewMonth, setViewMonth] = React.useState(
    () => selectedDate?.getMonth() ?? today.getMonth(),
  )
  const [hours, setHours] = React.useState(initHours)
  const [minutes, setMinutes] = React.useState(initMinutes)

  const [manualInput, setManualInput] = React.useState(() =>
    formatForDisplay(selectedDate, initHours, initMinutes, tenantTimeFormat),
  )
  const [inputFocused, setInputFocused] = React.useState(false)

  const manualInputRef = React.useRef<HTMLInputElement>(null)
  const pendingCaretRef = React.useRef<number | null>(null)
  // Set by the keydown digit-overwrite branch so the stray range-replacement change that a
  // real browser still fires for that keystroke is dropped instead of reflowed (REBUILD-473).
  const suppressNextChangeRef = React.useRef(false)

  React.useLayoutEffect(() => {
    const input = manualInputRef.current
    if (!input || input !== document.activeElement) return
    if (pendingCaretRef.current !== null) {
      const caret = pendingCaretRef.current
      pendingCaretRef.current = null
      input.setSelectionRange(caret, caret)
    }
  })

  React.useEffect(() => {
    if (!inputFocused) {
      setManualInput(formatForDisplay(selectedDate, initHours, initMinutes, tenantTimeFormat))
    }
  }, [value, inputFocused, selectedDate, initHours, initMinutes, tenantTimeFormat])

  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (open) {
      const base = selectedDate ?? today
      setViewYear(base.getFullYear())
      setViewMonth(base.getMonth())
      setHours(initHours)
      setMinutes(initMinutes)
    }
  }, [open])
  /* eslint-enable react-hooks/exhaustive-deps */

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11)
      setViewYear((y) => y - 1)
    } else setViewMonth((m) => m - 1)
  }
  const nextMonth = () => {
    if (maxDate) {
      const nextYear = viewMonth === 11 ? viewYear + 1 : viewYear
      const nextMo = viewMonth === 11 ? 0 : viewMonth + 1
      if (
        nextYear > maxDate.getFullYear() ||
        (nextYear === maxDate.getFullYear() && nextMo > maxDate.getMonth())
      ) {
        return
      }
    }
    if (viewMonth === 11) {
      setViewMonth(0)
      setViewYear((y) => y + 1)
    } else setViewMonth((m) => m + 1)
  }

  const isNextMonthDisabled = maxDate
    ? (viewMonth === 11 ? viewYear + 1 : viewYear) > maxDate.getFullYear() ||
      ((viewMonth === 11 ? viewYear + 1 : viewYear) === maxDate.getFullYear() &&
        (viewMonth === 11 ? 0 : viewMonth + 1) > maxDate.getMonth())
    : false

  const clampTimeIfMaxDay = (date: Date, h: string, m: string) => {
    if (!maxDate || !isSameDay(date, maxDate)) return { h, m }
    const maxH = maxDate.getHours()
    const maxM = maxDate.getMinutes()
    let numH = parseInt(h, 10) || 0
    let numM = parseInt(m, 10) || 0
    if (numH > maxH || (numH === maxH && numM > maxM)) {
      numH = maxH
      numM = maxM
    }
    return { h: String(numH), m: String(numM) }
  }

  const handleSelectDate = (date: Date) => {
    const effectiveHours = clampTimeIfMaxDay(date, hours, minutes).h
    const effectiveMinutes = clampTimeIfMaxDay(date, hours, minutes).m
    onChange(toDatetimeLocal(date, effectiveHours, effectiveMinutes))
  }

  const wallClockValue = toCanonicalWallClock(
    hours === '' ? 0 : Number.parseInt(hours, 10) || 0,
    minutes === '' ? 0 : Number.parseInt(minutes, 10) || 0,
  )

  const handleWallClockChange = (nextWallClock: string) => {
    const parts = parseWallClockParts(nextWallClock)
    if (!parts) return

    let nextHours = String(parts.hour)
    let nextMinutes = String(parts.minute)
    if (selectedDate) {
      const clamped = clampTimeIfMaxDay(selectedDate, nextHours, nextMinutes)
      nextHours = clamped.h
      nextMinutes = clamped.m
      onChange(toDatetimeLocal(selectedDate, nextHours, nextMinutes))
    }
    setHours(nextHours)
    setMinutes(nextMinutes)
  }

  const applyParsedManual = (parsed: { date: Date; hours: string; minutes: string } | null) => {
    if (!parsed) return
    if (maxDate && parsed.date > maxDate) return
    onChange(toDatetimeLocal(parsed.date, parsed.hours, parsed.minutes))
  }

  const handleManualInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (suppressNextChangeRef.current) {
      suppressNextChangeRef.current = false
      // The keydown digit-overwrite branch already applied this edit and called
      // preventDefault; a stray range-replacement change here would reflow the mask and
      // drop a digit on select-and-overtype (REBUILD-473). Re-assert the controlled value.
      setManualInput((current) => current)
      return
    }

    if (isTwelveHour) {
      setManualInput(event.target.value)
      applyParsedManual(parseMaskedInput12(event.target.value))
      return
    }

    const rawValue = event.target.value
    const rawCaret = event.target.selectionStart ?? rawValue.length
    const digitsBeforeCaret = rawValue.slice(0, rawCaret).replace(/\D/g, '').length
    const masked = applyMask24(rawValue)
    setManualInput(masked)
    pendingCaretRef.current = caretPositionInMasked24(masked, digitsBeforeCaret)
    applyParsedManual(parseMaskedInput24(masked))
  }

  const handleManualInputBlur = () => {
    setInputFocused(false)
    if (manualInput.trim() === '') {
      onChange('')
      return
    }
    const parsed = isTwelveHour ? parseMaskedInput12(manualInput) : parseMaskedInput24(manualInput)
    if (parsed) {
      applyParsedManual(parsed)
    } else {
      setManualInput(formatForDisplay(selectedDate, initHours, initMinutes, tenantTimeFormat))
    }
  }

  const handleManualInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur()
      return
    }

    const input = event.currentTarget
    const caret = input.selectionStart ?? 0
    const selectionEnd = input.selectionEnd ?? caret
    const isCollapsed = caret === selectionEnd

    // Backspace with collapsed caret: move caret to the previous digit slot so the user
    // can overwrite it, rather than deleting and shifting the whole masked string.
    if (isCollapsed && event.key === 'Backspace' && caret > 0) {
      event.preventDefault()
      const prevSlot = prevDigitSlot(manualInput, caret)
      input.setSelectionRange(prevSlot, prevSlot)
      return
    }

    // Digit overwrite: replace the digit at the next slot in place instead of inserting.
    // Works for both a collapsed caret and a range selection: nextDigitSlot starts from
    // selectionStart, overwrites one digit in place, and collapses the caret forward.
    // This prevents the browser default "replace selection" from corrupting the fixed-slot mask.
    // Falls through (no preventDefault) for 24h when beyond the current string — fresh entry
    // mode where applyMask24 in handleManualInputChange reflows the new digit correctly.
    // For 12h there is no mask reflow, so insertion beyond the last digit slot is always
    // blocked to prevent corrupting the fixed-width display (e.g. a third minute digit).
    if (/^\d$/.test(event.key)) {
      const slotIdx = nextDigitSlot(manualInput, caret)
      if (slotIdx < manualInput.length) {
        event.preventDefault()
        // A range-overtype keystroke still composes a stray range-replacement change in real
        // browsers despite preventDefault; suppress that next change so applyMask24 does not
        // reflow the mask and drop a digit (REBUILD-473). Only a real range selection produces
        // that stray change — a collapsed caret does not, so guard on !isCollapsed to avoid a
        // stuck flag that would swallow a later legitimate change.
        if (!isCollapsed) suppressNextChangeRef.current = true
        const nextCaret = slotIdx + 1
        // Same digit: value won't change, so React bails on re-render and the
        // useLayoutEffect never fires — advance the caret imperatively instead.
        if (event.key === manualInput[slotIdx]) {
          input.setSelectionRange(nextCaret, nextCaret)
          return
        }
        const newStr = manualInput.slice(0, slotIdx) + event.key + manualInput.slice(slotIdx + 1)
        setManualInput(newStr)
        pendingCaretRef.current = nextCaret
        if (isTwelveHour) {
          applyParsedManual(parseMaskedInput12(newStr))
        } else {
          applyParsedManual(parseMaskedInput24(newStr))
        }
        return
      }
      if (isTwelveHour) {
        event.preventDefault()
        return
      }
    }

    // 12h AM/PM toggle via 'a'/'p' keys.
    if (isTwelveHour && /^[apAP]$/.test(event.key) && /\s(AM|PM)$/i.test(manualInput)) {
      const newPeriod = event.key.toUpperCase().startsWith('A') ? 'AM' : 'PM'
      const newStr = manualInput.replace(/\s(AM|PM)$/i, ` ${newPeriod}`)
      setManualInput(newStr)
      pendingCaretRef.current = caret
      applyParsedManual(parseMaskedInput12(newStr))
      event.preventDefault()
    }

    // 12h: block all other printable characters (length === 1 distinguishes them from
    // control keys like ArrowLeft/Tab/Backspace) that are not digits or AM/PM toggles.
    // Skip when a modifier key is held so keyboard shortcuts (Cmd+C, Cmd+V, Cmd+A …) work.
    if (
      isTwelveHour &&
      !event.metaKey &&
      !event.ctrlKey &&
      event.key.length === 1 &&
      !/^\d$/.test(event.key) &&
      !/^[apAP]$/.test(event.key)
    ) {
      event.preventDefault()
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={cn('relative flex items-center', className)}>
        <input
          ref={manualInputRef}
          id={id}
          type="text"
          value={manualInput}
          onChange={handleManualInputChange}
          onFocus={() => setInputFocused(true)}
          onBlur={handleManualInputBlur}
          onKeyDown={handleManualInputKeyDown}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          required={required}
          aria-invalid={ariaInvalid}
          maxLength={isTwelveHour ? 19 : 16}
          className={cn(
            'h-9 w-full rounded-md border border-input bg-background px-3 py-1 pr-9 text-sm shadow-sm transition-colors',
            'placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring',
            disabled && 'cursor-not-allowed opacity-50',
            inputClassName,
          )}
        />
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Open calendar"
          >
            <CalendarIcon className="h-4 w-4" />
          </button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        className="w-auto p-3"
        align="start"
        onFocusOutside={(event) => event.preventDefault()}
        onPointerDownOutside={(event) => {
          // Portaled SelectContent (AM/PM) is outside PopoverContent; keep calendar open.
          // Pointer targets can be Text nodes — resolve to an Element before closest().
          const eventTarget = event.target
          const targetElement =
            eventTarget instanceof Element
              ? eventTarget
              : eventTarget instanceof Node
                ? eventTarget.parentElement
                : null
          if (targetElement?.closest('[data-slot="select-content"]')) {
            event.preventDefault()
          }
        }}
      >
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={prevMonth}
              className="rounded p-1 hover:bg-accent"
              aria-label="Previous month"
            >
              ‹
            </button>
            <span className="text-sm font-medium">
              {MONTHS_FULL[viewMonth]} {viewYear}
            </span>
            <button
              type="button"
              onClick={nextMonth}
              disabled={isNextMonthDisabled}
              className={cn(
                'rounded p-1 hover:bg-accent',
                isNextMonthDisabled && 'opacity-30 cursor-not-allowed',
              )}
              aria-label="Next month"
            >
              ›
            </button>
          </div>

          <CalendarGrid
            viewYear={viewYear}
            viewMonth={viewMonth}
            selected={selectedDate}
            today={today}
            maxDate={maxDate}
            onSelect={handleSelectDate}
          />

          <div className="flex items-center justify-center border-t pt-3">
            <TenantTimeInput
              value={wallClockValue}
              onChange={handleWallClockChange}
              tenantTimeFormat={tenantTimeFormat}
            />
          </div>

          {showNowButton && (
            <div className="flex justify-end border-t pt-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  const now = timeZone ? nowInTimeZone(timeZone) : new Date()
                  // Respect maxDate: clamp "now" so it never exceeds the upper bound.
                  const effectiveNow = maxDate && now > maxDate ? maxDate : now
                  const h = String(effectiveNow.getHours()).padStart(2, '0')
                  const m = String(effectiveNow.getMinutes()).padStart(2, '0')
                  setHours(h)
                  setMinutes(m)
                  onChange(toDatetimeLocal(effectiveNow, h, m))
                  setOpen(false)
                }}
              >
                {t('shared.dateTimePicker.now')}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
